<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'next/icon/Icon.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import TicketsAPI from 'dashboard/api/tickets';
import { getFileInfo } from '@chatwoot/utils';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  ticket: {
    type: Object,
    required: true,
  },
  linkedAttachmentIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close', 'select']);

const { t } = useI18n();
const isLoading = ref(true);
const attachments = ref([]);

const availableAttachments = computed(() => {
  return attachments.value.filter(
    attachment => !props.linkedAttachmentIds.includes(attachment.id)
  );
});

const getFileDetails = dataUrl => {
  return getFileInfo(dataUrl || '');
};

const getDisplayFileName = dataUrl => {
  const { base, type } = getFileDetails(dataUrl);
  const truncatedName = (str, maxLength, hasExt) =>
    str.length > maxLength
      ? `${str.substring(0, maxLength).trimEnd()}${hasExt ? '..' : '...'}`
      : str;

  return type
    ? `${truncatedName(base, 16, true)}.${type}`
    : truncatedName(base, 18, false);
};

const getFileType = attachment => {
  if (attachment.file_type === 'image') return 'image';
  if (attachment.file_type === 'video') return 'video';
  if (attachment.file_type === 'audio') return 'audio';
  return getFileDetails(attachment.data_url).type || 'file';
};

const isImageType = attachment => attachment.file_type === 'image';

const fetchAttachments = async () => {
  isLoading.value = true;
  try {
    const response = await TicketsAPI.getConversationAttachments(
      props.ticket.display_id
    );
    attachments.value = response.data.attachments || [];
  } catch (error) {
    attachments.value = [];
  } finally {
    isLoading.value = false;
  }
};

const selectAttachment = attachment => {
  emit('select', attachment);
};

const close = () => {
  emit('close');
};

onMounted(() => {
  fetchAttachments();
});
</script>

<template>
  <Modal :show="show" :on-close="close">
    <div class="p-6">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
        {{ $t('TICKETS.ATTACHMENTS.SELECT_FROM_CONVERSATION') }}
      </h3>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center py-8">
        <Spinner />
      </div>

      <!-- Attachments Grid -->
      <div
        v-else-if="availableAttachments.length"
        class="grid grid-cols-3 gap-3 max-h-96 overflow-y-auto"
      >
        <div
          v-for="attachment in availableAttachments"
          :key="attachment.id"
          class="p-2 border border-n-weak rounded-lg cursor-pointer hover:border-n-brand hover:bg-n-alpha-1 transition-colors"
          @click="selectAttachment(attachment)"
        >
          <!-- Image preview -->
          <div
            v-if="isImageType(attachment)"
            class="w-full h-24 overflow-hidden rounded-md mb-2"
          >
            <img
              class="object-cover w-full h-full"
              :src="attachment.thumb_url || attachment.data_url"
              :alt="getDisplayFileName(attachment.data_url)"
            />
          </div>

          <!-- File preview -->
          <div
            v-else
            class="w-full h-24 flex items-center justify-center bg-n-alpha-1 rounded-md mb-2"
          >
            <FileIcon
              class="size-10"
              :file-type="getFileType(attachment)"
            />
          </div>

          <!-- File name -->
          <p
            class="text-xs text-n-slate-12 truncate"
            :title="getFileDetails(attachment.data_url).name"
          >
            {{ getDisplayFileName(attachment.data_url) }}
          </p>
          <p class="text-xs text-n-slate-10 capitalize">
            {{ attachment.file_type }}
          </p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-8">
        <Icon icon="i-lucide-paperclip" class="size-10 text-n-slate-10 mx-auto mb-3" />
        <p class="text-n-slate-10">
          {{ $t('TICKETS.ATTACHMENTS.NO_CONVERSATION_ATTACHMENTS') }}
        </p>
      </div>

      <!-- Footer -->
      <div class="flex justify-end mt-4 pt-4 border-t border-n-weak">
        <Button variant="ghost" @click="close">
          {{ $t('TICKETS.FORM.CANCEL') }}
        </Button>
      </div>
    </div>
  </Modal>
</template>
